import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { createSelector } from "reselect";

import { BaseResponse, Pagination, QueryModel, Response, ResponseFieldError, ResponseList } from "src/app/models/api.types";

import { useApiParams, useFileDownload } from "src/app/hooks";
import { URL_CAMPAIGN_LUCKY_DRAW, URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT, URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_COUPONS, URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_DETAIL, URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_SUMMARY, URL_CAMPAIGN_LUCKY_DRAW_PRIZES, deleteLuckyDraw, deleteLuckyDrawPrize, getExportListParticipant, getLuckyDrawDetail, getLuckyDrawList, getLuckyDrawParticipantCouponList, getLuckyDrawParticipantList, getLuckyDrawParticipantSummary, getLuckyDrawPrizeList, getLuckyDrawRule, getParticipantDetail, saveLuckyDrawGeneral, saveLuckyDrawRule, submitPrintCoupon, submitPrize, submitSummaryLuckyDraw, submitPostPrintCoupon, URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_EXPORT, getLuckyDrawDetailDrawing, URL_CAMPAIGN_LUCKY_DRAW_DRAWING_DETAIL, submitLuckyDrawDrawing, getListWinners, URL_CAMPAIGN_LUCKY_DRAW_WINNERS, URL_CAMPAIGN_LUCKY_DRAW_WINNERS_EXPORT, URL_CAMPAIGN_LUCKY_DRAW_SET_AS_WINNERS, getExportListWinners, URL_CAMPAIGN_LUCKY_DRAW_DRAWING_DETAIL_PRIZE_LIST, getLuckyDrawDetailDrawingPrizeList, URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_EXPORT_COUPON_DETAIL, getExportParticipantCouponDetail, URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_EXPORT_COUPON_SUMMARY, getExportParticipantCouponSummary } from "../services/Campaign.service";

import { DrawingModel, LuckyDrawDetailModel, LuckyDrawModel, LuckyDrawParticipantModel, LuckyDrawRuleMasterModel, LuckyDrawRuleModel, ParticipantCouponModel, ParticipantDetailModel, ParticipantSummaryModel, PrizeListModel, WinnerModel, WinnersModel } from '../models/LuckyDraw.model';
import { defaultLuckyDrawPrizes, defaultLuckyDrawPrizeTranx, defaultLuckyDrawRuleValues } from "../page/Campaign/LuckyDrawForm/LuckyDrawForm3.hook";

export const selectList = createSelector(
  (state: AxiosResponse<BaseResponse<Pagination<any[]>>>) => state.data,
  (data: BaseResponse<Pagination<any[]>>) => data.response_output?.list
);
export const selectDetail = createSelector(
  (state: AxiosResponse<BaseResponse<Response<any>>>) => state.data,
  (data: BaseResponse<Response<any>>) => data.response_output?.detail
);

export function useGetLuckyDraw(param?: QueryModel) {

  const defaultParams = {
    directory_ids: '',
    period: '',
    categories: ''
  };
  const [_params] = useApiParams({ defaultValues: defaultParams });
  const params = { ...param, ..._params };

  return useQuery<
    AxiosResponse<BaseResponse<Pagination<LuckyDrawModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<LuckyDrawModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW, params],
    queryFn: (_) => getLuckyDrawList(params, _.signal),
    select: selectList,
  });
}

export function useGetPrizeList(campaign_id: string, param?: QueryModel) {

  const defaultParams = {
    directory_ids: '',
    draw_date: '',
    types: ''
  };
  const [_params] = useApiParams({ defaultValues: defaultParams });
  const params = { ...param, ..._params };
  return useGetPrizeMasterList(campaign_id, params);
}

export function useGetPrizeMasterList(campaign_id: string, param?: QueryModel, additional_param?: object) {
  const params = { ...param, ...additional_param };
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<PrizeListModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<PrizeListModel[]> | undefined
  >({
    keepPreviousData: true,
    enabled: Boolean(campaign_id && campaign_id !== 'new'),
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_PRIZES(campaign_id), params],
    queryFn: (_) => getLuckyDrawPrizeList(params, campaign_id, _.signal),
    select: selectList,
  });
}

export const useSubmitPrize = (campaign_id: string) => {
  const refetchList = useRefetchFeatured([URL_CAMPAIGN_LUCKY_DRAW_PRIZES(campaign_id)]);
  return useMutation<
    AxiosResponse<BaseResponse<Response<PrizeListModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    any
  >({
    mutationKey: [URL_CAMPAIGN_LUCKY_DRAW_PRIZES(campaign_id)],
    mutationFn: (payload) => submitPrize(campaign_id, payload),
    onSuccess: refetchList
  })
}

export const useDeletePrize = (campaign_id: string) => {
  const refetchList = useRefetchFeatured([URL_CAMPAIGN_LUCKY_DRAW_PRIZES(campaign_id)]);
  return useMutation<
    AxiosResponse<BaseResponse<Response<PrizeListModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    { campaign_id: string, prize_id: string } // Pass `prize_id` as a variable when calling mutate
  >({
    mutationFn: ({ campaign_id, prize_id }) => deleteLuckyDrawPrize(campaign_id, prize_id),
    onSuccess: refetchList
  });
}

export function useRefetchFeatured(queryKey: any) {
  const queryClient = useQueryClient()

  return () => {
    const key = Array.isArray(queryKey) ? queryKey : [queryKey];
    queryClient.invalidateQueries({ queryKey: key })
  }
}

export function useSaveLuckyDrawGeneral() {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<BaseResponse<Response<LuckyDrawModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    LuckyDrawModel
  >({
    mutationKey: [URL_CAMPAIGN_LUCKY_DRAW],
    mutationFn: saveLuckyDrawGeneral,
    onSuccess: (res, variables) => {
      const id = variables.id || res.data?.response_output?.detail?.id;
      if (id) {
        queryClient.invalidateQueries({ queryKey: [`${URL_CAMPAIGN_LUCKY_DRAW}/${id}`] });
      }
    },
  });
}

export const useSaveLuckyDrawRule = (campaign_id: string) => {
  return useMutation<
    AxiosResponse<BaseResponse<Pagination<PrizeListModel[]>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    PrizeListModel[]
  >({
    mutationKey: [`${URL_CAMPAIGN_LUCKY_DRAW}/${campaign_id}/rules`],
    mutationFn: (payload) => saveLuckyDrawRule(campaign_id, payload),
  })
}

export function useSubmitSummaryLuckyDraw() {
  return useMutation<
    AxiosResponse<BaseResponse<Response<LuckyDrawModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    string
  >({
    mutationFn: (campaign_id) => submitSummaryLuckyDraw(campaign_id)
  });
}

export function useDeleteLuckyDraw() {
  return useMutation<
    AxiosResponse<BaseResponse<Response<LuckyDrawModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    string // Pass `id` as a variable when calling mutate
  >({
    mutationFn: (id) => deleteLuckyDraw(id), // Correct function reference
  });
}


export function useGetLuckyDrawDetailGeneralInfo(id?: string, is_full_info?: boolean) {
  return useQuery<
    AxiosResponse<BaseResponse<Response<LuckyDrawDetailModel>>>,
    AxiosError<BaseResponse<Response<LuckyDrawDetailModel>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [`${URL_CAMPAIGN_LUCKY_DRAW}/${id}`],
    queryFn: (_) => getLuckyDrawDetail(id ?? '', { is_full_info }, _.signal),
    select: selectDetail,
    enabled: !!id
  });
}

const selectLuckyDrawRules = createSelector(
  (state: AxiosResponse<BaseResponse<Pagination<LuckyDrawRuleModel[]>>>) => state.data,
  (data: BaseResponse<Pagination<LuckyDrawRuleModel[]>>) => {
    const content = data.response_output?.list?.content?.length
      ? data.response_output.list.content
      : defaultLuckyDrawRuleValues.prize_rules;

    content.forEach((rule) => {
      if (rule.prize_transaction_rules && rule.prize_transaction_rules.length > 0) {
        rule.prize_transaction_rules = rule.prize_transaction_rules.map((trx) => ({
          ...trx,
          prizes: (trx.prizes?.length ? trx.prizes : defaultLuckyDrawPrizes).map((item) => {
            let updatedPrizes;
            if (item.type === "CONSOLATION") {
              updatedPrizes = { id: "CONSOLATION", name: "CONSOLATION" };
            } else {
              updatedPrizes = item.prizes ? (item.prizes as any)[0] : undefined;
            }
            return {
              ...item,
              prizes: updatedPrizes,
            };
          })
        }));
      } else {
        rule.prize_transaction_rules = defaultLuckyDrawPrizeTranx;
      }
    });
    return { prize_rules: content } as LuckyDrawRuleMasterModel
  }
);
export function useGetLuckyDrawRules(id?: string) {
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<LuckyDrawRuleModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    LuckyDrawRuleMasterModel | undefined
  >({
    keepPreviousData: true,
    queryKey: [`${URL_CAMPAIGN_LUCKY_DRAW}/${id}/rules`],
    queryFn: (_) => getLuckyDrawRule(id ?? '', _.signal),
    select: selectLuckyDrawRules,
    enabled: !!id,
  });
}

export function useLuckyDrawParticipantList(campaign_id: string, param?: QueryModel) {
  const defaultParams = {
    directory_ids: '',
    period: '',
    categories: ''
  };
  const [_params] = useApiParams({ defaultValues: defaultParams });
  const params = { ...param, ..._params };

  return useQuery<
    AxiosResponse<BaseResponse<Pagination<LuckyDrawParticipantModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<LuckyDrawParticipantModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT(campaign_id), params],
    queryFn: (_) => getLuckyDrawParticipantList(campaign_id, params, _.signal),
    select: selectList,
  });
}

// useExportParticipantList
export function useExportParticipantList(campaign_id: string) {
  const defaultValues = {
    search: '',
    sort: '',
    order: ''
  };
  const [_params] = useApiParams({
    defaultValues,
  });
  const params = {
    ..._params,
    page: undefined,
    limit: undefined
  };

  return useFileDownload<unknown>({
    mutationKey: [URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_EXPORT(campaign_id)],
    mutationFn: (_) => getExportListParticipant(campaign_id, { ...params })
  });
}

export function useExportParticipantCouponDetail(campaign_id: string) {
  const defaultValues = {
    search: '',
    sort: '',
    order: ''
  };
  const [_params] = useApiParams({
    defaultValues,
  });
  const params = {
    ..._params,
    page: undefined,
    limit: undefined
  };

  return useFileDownload<unknown>({
    mutationKey: [URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_EXPORT_COUPON_DETAIL(campaign_id)],
    mutationFn: (_) => getExportParticipantCouponDetail(campaign_id, { ...params })
  });
}

export function useExportParticipantCouponSummary(campaign_id: string) {
  const defaultValues = {
    search: '',
    sort: '',
    order: ''
  };
  const [_params] = useApiParams({
    defaultValues,
  });
  const params = {
    ..._params,
    page: undefined,
    limit: undefined
  };

  return useFileDownload<unknown>({
    mutationKey: [URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_EXPORT_COUPON_SUMMARY(campaign_id)],
    mutationFn: (_) => getExportParticipantCouponSummary(campaign_id, { ...params })
  });
}

export function useGetLuckyDrawDetailParticipantSummary(campaign_id: string) {
  return useQuery<
    AxiosResponse<BaseResponse<Response<ParticipantSummaryModel>>>,
    AxiosError<BaseResponse<Response<ParticipantSummaryModel>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_SUMMARY(campaign_id)],
    queryFn: (_) => getLuckyDrawParticipantSummary(campaign_id, _.signal),
    select: selectDetail,
    enabled: !!campaign_id
  });
}

export function useGetLuckyDrawDetailParticipantDetail(campaign_id: string, participant_id: string) {
  return useQuery<
    AxiosResponse<BaseResponse<Response<ParticipantDetailModel>>>,
    AxiosError<BaseResponse<Response<ParticipantDetailModel>>>,
    ParticipantDetailModel | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_DETAIL(campaign_id, participant_id)],
    queryFn: (_) => getParticipantDetail(campaign_id, participant_id, _.signal),
    select: selectDetail,
    enabled: !!participant_id && !!campaign_id
  });
}

export function useLuckyDrawParticipantCouponList(campaign_id: string, participant_id: string, param?: QueryModel) {

  const [_params] = useApiParams();
  const params = { ...param, ..._params };

  return useQuery<
    AxiosResponse<BaseResponse<Pagination<ParticipantCouponModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<ParticipantCouponModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_COUPONS(campaign_id, participant_id), params],
    queryFn: (_) => getLuckyDrawParticipantCouponList(campaign_id, participant_id, params, _.signal),
    select: selectList,
  });
}

export function useLuckyDrawParticipantReprintCouponList(campaign_id: string, participant_id: string, param?: QueryModel) {
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<ParticipantCouponModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<ParticipantCouponModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_PARTICIPANT_COUPONS(campaign_id, participant_id), param],
    queryFn: (_) => getLuckyDrawParticipantCouponList(campaign_id, participant_id, param, _.signal),
    select: selectList,
  });
}

export const useSubmitPrintCoupon = (campaign_id: string, participant_id: string) => {
  return useMutation<
    AxiosResponse<BaseResponse<Pagination<ParticipantCouponModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    any
  >({
    mutationFn: ({ action_type, payload }) => submitPrintCoupon(campaign_id, participant_id, action_type, payload),
  })
}

export const useSubmitPostPrintCoupon = (campaign_id: string, participant_id: string) => {
  return useMutation<
    AxiosResponse<BaseResponse<Pagination<ParticipantCouponModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    any
  >({
    mutationFn: (payload) => submitPostPrintCoupon(campaign_id, participant_id, payload),
  })
}

export function useGetLuckyDrawDetailDrawing(id: string) {
  const [_params] = useApiParams();
  const params = { ..._params };
  return useQuery<
    AxiosResponse<BaseResponse<Response<DrawingModel>>>,
    AxiosError<BaseResponse<Response<DrawingModel>>>,
    any | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_DRAWING_DETAIL(id), params.search],
    queryFn: (_) => getLuckyDrawDetailDrawing(id ?? '',  {search: params.search}, _.signal),
    select: selectDetail,
    enabled: (!!id && !!params.search)
  });
}

export function useGetLuckyDrawDetailDrawingPrizeList(campaign_id: string, member_coupon_id: string, params?: object) {
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<PrizeListModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<PrizeListModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_DRAWING_DETAIL_PRIZE_LIST(campaign_id, member_coupon_id), params],
    queryFn: (_) => getLuckyDrawDetailDrawingPrizeList(params, campaign_id, member_coupon_id, _.signal),
    select: selectList,
    enabled: (!!campaign_id && !!member_coupon_id)
  });
}

export function useLuckyDrawDrawingSubmit(id: string) {

  return useMutation<
    AxiosResponse<BaseResponse<Response<DrawingModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    {prize_id?: string, member_coupon_id?: string}
  >({
    mutationKey: [URL_CAMPAIGN_LUCKY_DRAW_SET_AS_WINNERS(id)],
    mutationFn: (payload) => submitLuckyDrawDrawing(id, payload)
  });
}

export function useListWinnersGrandprize(campaign_id: string) {
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<WinnersModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<WinnersModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_WINNERS(campaign_id, 'grand-prize')],
    queryFn: (_) => getListWinners('grand-prize', campaign_id, _.signal),
    select: selectList,
  });
}

export function useListWinnersConsolation(campaign_id: string, param?: QueryModel) {
  const defaultParams = {
    directory_ids: '',
  };
  const [_params] = useApiParams({ defaultValues: defaultParams });

  const params = {
    ..._params,
    page: undefined,
    limit: undefined
  };
  return useQuery<
    AxiosResponse<BaseResponse<Pagination<WinnerModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<WinnerModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [URL_CAMPAIGN_LUCKY_DRAW_WINNERS(campaign_id, 'consolation'), params.directory_ids],
    queryFn: (_) => getListWinners('consolation', campaign_id, params, _.signal),
    select: selectList,
  });
}

// useExportListWinners
export function useExportListWinners(campaign_id: string) {
  const defaultValues = {
    directory_ids: '',
  };
  const [_params] = useApiParams({
    defaultValues,
  });
  const params = {
    ..._params,
    page: undefined,
    limit: undefined
  };

  return useFileDownload<unknown>({
    mutationKey: [URL_CAMPAIGN_LUCKY_DRAW_WINNERS_EXPORT(campaign_id)],
    mutationFn: (_) => getExportListWinners(campaign_id, { ...params })
  });
}